import React, { useEffect, useState, memo, useRef } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import { setBranchId } from '../../../auth/actions';
import { Input, Button, Dropdown, Loading, Toggle } from '../../../../common/ui';
import PropTypes from 'prop-types';
import Switch from '../../../../common/components/Switch';
import { useNavigate } from 'react-router-dom';
import { showAlert } from '../../../../common/actions';
import { findReceiptSettings, updateReceiptSettings } from '../actions';
import Axios from '../../../../common/util/Axios';

const receiptTemplateLists = [
  { id: '80MM_RECEIPT_ENGLISH_TEMPLATE_DEFAULT', name: '80MM_RECEIPT_ENGLISH_TEMPLATE_DEFAULT' },
  { id: '80MM_RECEIPT_ENGLISH_TEMPLATE_2', name: '80MM_RECEIPT_ENGLISH_TEMPLATE_2' },
  { id: '80MM_BILL_RECEIPT_SINHALA_TEMPLATE_DEFAULT', name: '80MM_BILL_RECEIPT_SINHALA_TEMPLATE_DEFAULT' },
  { id: '80MM_BILL_RECEIPT_SINHALA_TEMPLATE_2', name: '80MM_BILL_RECEIPT_SINHALA_TEMPLATE_2' },
  { id: 'A4_INVOICE_ENGLISH_TEMPLATE_DEFAULT', name: 'A4_INVOICE_ENGLISH_TEMPLATE_DEFAULT' },
  { id: 'A4_INVOICE_ENGLISH_TEMPLATE_HEADER_&_FOOTER_LESS', name: 'A4_INVOICE_ENGLISH_TEMPLATE_HEADER_&_FOOTER_LESS' },
  { id: '240MM_140MM_INVOICE_ENGLISH_TEMPLATE_WITH_WARRANTY', name: '240MM_140MM_INVOICE_ENGLISH_TEMPLATE_WITH_WARRANTY' },
  { id: '72MM_RECEIPT_ENGLISH_TEMPLATE_DEFAULT', name: '72MM_RECEIPT_ENGLISH_TEMPLATE_DEFAULT' },
  { id: '80MM_RECEIPT_ENGLISH_TEMPLATE_RESTAURANT', name: '80MM_RECEIPT_ENGLISH_TEMPLATE_RESTAURANT' },
  { id: '72MM_RECEIPT_ENGLISH_TEMPLATE_RESTAURANT', name: '72MM_RECEIPT_ENGLISH_TEMPLATE_RESTAURANT' },
  { id: 'A4_INVOICE_ENGLISH_TEMPLATE_WITH_WARRANTY', name: 'A4_INVOICE_ENGLISH_TEMPLATE_WITH_WARRANTY' },
];

const templateOptions = receiptTemplateLists.map((t) => ({ value: t.id, label: t.name }));

const ReceiptSettings = memo(
  ({
    setBranchId,
    auth: { branchList, branchId, companyId },
    receiptSettingsState: { receiptSettingsData },
    showAlert,
    findReceiptSettings,
    updateReceiptSettings,
  }) => {
    const originalDataRef = useRef({});
    const [receiptSettingsForm, setReceiptSettingsForm] = useState({
      'shop-name': '',
      'shop-address': '',
      'shop-contact-number': '',
      'receipt-no-prefix': '',
      'bill-description-1': '',
      'bill-description-2': '',
      'is-show-optional-description-1': '',
      'optional-description-1': '',
      'bill-receipt-template': '',
      'secondary-printer-template': '',
      'is-sms-receipt-enabled': '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [headerVertical, setHeaderVertical] = useState('DEFAULT');
    const [verticalOptions, setVerticalOptions] = useState([{ value: 'DEFAULT', label: 'Default' }]);
    const navigate = useNavigate();

    useEffect(() => {
      if (branchId) {
        findReceiptSettings(branchId);
      }
    }, []);

    useEffect(() => {
      if (
        Array.isArray(receiptSettingsData) &&
        receiptSettingsData.length > 0
      ) {
        const nextForm = {
          'shop-name': '',
          'shop-address': '',
          'shop-contact-number': '',
          'receipt-no-prefix': '',
          'bill-description-1': '',
          'bill-description-2': '',
          'is-show-optional-description-1': '',
          'optional-description-1': '',
          'bill-receipt-template': '',
          'secondary-printer-template': '',
          'is-sms-receipt-enabled': '',
        };
        for (const item of receiptSettingsData) {
          const paramKey = item?.param;
          const paramValue = item?.value;
          if (
            paramKey &&
            Object.prototype.hasOwnProperty.call(nextForm, paramKey)
          ) {
            nextForm[paramKey] = paramValue ?? '';
          }
        }
        originalDataRef.current = { ...nextForm };
        setReceiptSettingsForm(nextForm);
      }
    }, [receiptSettingsData]);

    useEffect(() => {
      if (branchId) {
        findReceiptSettings(branchId, headerVertical);
      }
    }, [branchId, headerVertical]);

    useEffect(() => {
      if (!companyId) return;
      Axios.get('/api/dashboard/settings/business-verticals', { params: { companyId } })
        .then((res) => {
          const rows = Array.isArray(res.data?.data) ? res.data.data : [];
          const custom = rows.filter((row) => {
            const code = String(row.code || '').toUpperCase();
            const active = row.isActive === true || row.isActive === 1;
            return active && code !== 'RETAIL' && code !== 'WHOLESALE' && code !== 'HIRE_PURCHASE';
          });
          setVerticalOptions([
            { value: 'DEFAULT', label: 'Default' },
            ...custom.map((row) => ({ value: row.code, label: row.name || row.code })),
          ]);
        })
        .catch(() => {
          setVerticalOptions([{ value: 'DEFAULT', label: 'Default' }]);
        });
    }, [companyId]);

    const handleChangeBranch = (value) => {
      setBranchId(value);
    };

    const handleChangeInputValues = (e) => {
      const { id, value } = e.target;
      if (id && Object.prototype.hasOwnProperty.call(receiptSettingsForm, id)) {
        setReceiptSettingsForm((prev) => ({ ...prev, [id]: value }));
      }
    };

    const handleToggleChange = (event) => {
      setReceiptSettingsForm({
        ...receiptSettingsForm,
        [event.target.name]: event.target.checked ? 'true' : 'false',
      });
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsLoading(true);
      const changedFields = {};
      Object.keys(receiptSettingsForm).forEach((key) => {
        const currentVal = receiptSettingsForm[key] ?? '';
        const originalVal = originalDataRef.current[key] ?? '';
        if (currentVal !== originalVal) {
          changedFields[key] = currentVal;
        }
      });

      if (Object.keys(changedFields).length === 0) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'No changes to save!',
        });
        setIsLoading(false);
        return;
      }

      const receiptSettings = {
        receiptSettingsData: changedFields,
        branchId: branchId,
        businessVerticalCode: headerVertical,
      };
      const result = await updateReceiptSettings(receiptSettings);
      if (result) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'Receipt settings updated successfully!',
        });
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Failed to update receipt settings!',
        });
      }
      setIsLoading(false);
      findReceiptSettings(branchId, headerVertical);
    };

    const onClickCancel = () => {
      navigate('/');
    };

    const branchOptions = (branchList || []).map((b) => ({ value: b.id, label: b.name }));

    return (
      <div className={styles.container}>
        <header className={styles.pageHeader}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>Receipt Settings</h1>
            <p className={styles.breadcrumb}>Settings / Receipt</p>
          </div>
          {branchList && branchList.length > 0 && branchId && (
            <div className={styles.branchSelectContainer}>
              <Dropdown
                id="branch-select"
                label="Branch"
                value={branchId}
                onChange={handleChangeBranch}
                options={branchOptions}
                placeholder="Select branch"
              />
            </div>
          )}
        </header>
        <div className={styles.body}>
          <div className={styles.formCard}>
            {isLoading && <Loading variant="overlay" label="" />}
            <form onSubmit={handleSubmit} noValidate>
              <div className={styles.topDiv}>
                <div className={styles.topicDiv}>
                  <h2 className={styles.topicLabel}>Receipt Header</h2>
                  <p className={styles.topicCaption}>
                    The top part of a receipt showing business details. Default is the shop header. A custom vertical keeps its own header.
                  </p>
                </div>
                <div className={styles.rowDiv}>
                  <Dropdown
                    id="receipt-header-vertical"
                    label="Vertical"
                    value={headerVertical}
                    onChange={(value) => setHeaderVertical(value || 'DEFAULT')}
                    options={verticalOptions}
                  />
                </div>
                <div className={styles.rowDiv}>
                  <Input
                    id="shop-name"
                    label="Company Name"
                    type="text"
                    value={receiptSettingsForm['shop-name']}
                    onInput={handleChangeInputValues}
                  />
                  <Input
                    id="shop-address"
                    label="Branch Address"
                    type="text"
                    value={receiptSettingsForm['shop-address']}
                    onInput={handleChangeInputValues}
                  />
                </div>
                <div className={styles.rowDiv}>
                  <Input
                    id="shop-contact-number"
                    label="Branch Contact"
                    type="text"
                    value={receiptSettingsForm['shop-contact-number']}
                    onInput={handleChangeInputValues}
                  />
                  <Input
                    id="receipt-no-prefix"
                    label="Receipt Number Prefix"
                    type="text"
                    value={receiptSettingsForm['receipt-no-prefix']}
                    onInput={handleChangeInputValues}
                    disabled={headerVertical !== 'DEFAULT'}
                  />
                </div>
                <div className={styles.rowDiv}>
                  <div className={styles.textInputContainer}>
                    <label htmlFor="bill-description-1" className={styles.textInputLabel}>
                      Bill Description 1
                    </label>
                    <textarea
                      id="bill-description-1"
                      className={styles.textarea}
                      rows={3}
                      value={receiptSettingsForm['bill-description-1']}
                      onChange={handleChangeInputValues}
                      placeholder=""
                    />
                  </div>
                  <div className={styles.textInputContainer}>
                    <label htmlFor="bill-description-2" className={styles.textInputLabel}>
                      Bill Description 2
                    </label>
                    <textarea
                      id="bill-description-2"
                      className={styles.textarea}
                      rows={3}
                      value={receiptSettingsForm['bill-description-2']}
                      onChange={handleChangeInputValues}
                      placeholder=""
                    />
                  </div>
                </div>
                <div className={styles.rowDiv}>
                  <Toggle
                    label="Show Main Description on Bill"
                    labelPlacement="start"
                    name="is-show-optional-description-1"
                    checked={receiptSettingsForm['is-show-optional-description-1'] === 'true'}
                    onChange={handleToggleChange}
                    fontSize={16}
                  />
                </div>
                {receiptSettingsForm['is-show-optional-description-1'] === 'true' && (
                  <div className={styles.rowDiv}>
                    <div className={styles.textInputContainer}>
                      <label htmlFor="optional-description-1" className={styles.textInputLabel}>
                        Main Description
                      </label>
                      <textarea
                        id="optional-description-1"
                        className={styles.textarea}
                        rows={3}
                        value={receiptSettingsForm['optional-description-1']}
                        onChange={handleChangeInputValues}
                        placeholder=""
                      />
                    </div>
                  </div>
                )}
                <div className={styles.line} />

                <div className={styles.topicDiv}>
                  <h2 className={styles.topicLabel}>Bill Receipt Settings</h2>
                  <p className={styles.topicCaption}>
                    Manage your bill receipt details
                  </p>
                </div>
                <div className={styles.rowDiv}>
                  <Dropdown
                    id="bill-receipt-template"
                    label="Bill Receipt Template"
                    value={receiptSettingsForm['bill-receipt-template']}
                    onChange={(value) =>
                      setReceiptSettingsForm((prev) => ({ ...prev, 'bill-receipt-template': value }))
                    }
                    options={templateOptions}
                    placeholder="Select a template"
                  />
                </div>
                <div className={styles.rowDiv}>
                  <Dropdown
                    id="secondary-printer-template"
                    label="Secondary Printer Template"
                    value={receiptSettingsForm['secondary-printer-template']}
                    onChange={(value) =>
                      setReceiptSettingsForm((prev) => ({ ...prev, 'secondary-printer-template': value }))
                    }
                    options={templateOptions}
                    placeholder="Select a template"
                  />
                </div>
                <div className={styles.rowDiv}>
                  {/* <Switch
                    checked={receiptSettingsForm['is-sms-receipt-enabled'] === 'true'}
                    onChange={handleToggleChange}
                    name="is-sms-receipt-enabled"
                    label="SMS Receipt Enable"
                    labelPlacement="start"
                    fontSize={16}
                  /> */}
                  <Toggle
                    label="SMS Receipt Enable"
                    labelPlacement="start"
                    name="is-sms-receipt-enabled"
                    checked={receiptSettingsForm['is-sms-receipt-enabled'] === 'true'}
                    onChange={handleToggleChange}
                    fontSize={16}
                  />
                </div>
              </div>

              <div className={styles.buttonPanelContainer}>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save'}
                </Button>
                <Button type="button" variant="secondary" onClick={onClickCancel}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }
);

ReceiptSettings.propTypes = {
  setBranchId: PropTypes.func.isRequired,
  auth: PropTypes.shape({
    branchList: PropTypes.array,
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  receiptSettingsState: PropTypes.shape({
    receiptSettingsData: PropTypes.array,
  }).isRequired,
  showAlert: PropTypes.func.isRequired,
  findReceiptSettings: PropTypes.func.isRequired,
  updateReceiptSettings: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.authState,
  receiptSettingsState: state.receiptSettingsState,
});

export default connect(mapStateToProps, {
  setBranchId,
  showAlert,
  updateReceiptSettings,
  findReceiptSettings,
})(ReceiptSettings);
